import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { QueryConfig } from '@/lib/query';
import { queryKeys } from '@/lib/query';
import { showSuccess } from '@/lib/utils/toast';
import type { PagedResult } from '@/types/api';

import { chatbotApi } from '../api/chatbotApi';
import type {
  Chatbot,
  ChatbotCreatePayload,
  ChatbotSearchParams,
  ChatbotUpdatePayload,
  ChatbotUser,
  ChatbotUserSearchParams,
  MyChatbot,
} from '../types/chatbot';

export function useChatbots(
  params?: ChatbotSearchParams,
  config?: QueryConfig<PagedResult<Chatbot>>,
) {
  return useQuery({
    queryKey: queryKeys.chatbots.list(
      params as Record<string, unknown> | undefined,
    ),
    queryFn: () => chatbotApi.getChatbots(params),
    placeholderData: keepPreviousData,
    ...config,
  });
}

export function useChatbot(id: string, config?: QueryConfig<Chatbot>) {
  return useQuery({
    queryKey: queryKeys.chatbots.detail(id),
    queryFn: () => chatbotApi.getChatbot(id),
    enabled: !!id && id !== 'create',
    ...config,
  });
}

export function useChatbotUsers(
  params: ChatbotUserSearchParams,
  config?: QueryConfig<PagedResult<ChatbotUser>>,
) {
  return useQuery({
    queryKey: queryKeys.chatbots.users(
      params.ChatbotId,
      params as unknown as Record<string, unknown>,
    ),
    queryFn: () => chatbotApi.getChatbotUsers(params),
    enabled: !!params.ChatbotId,
    placeholderData: keepPreviousData,
    ...config,
  });
}

export function useMyChatbots(config?: QueryConfig<MyChatbot[]>) {
  return useQuery({
    queryKey: queryKeys.chatbots.my(),
    queryFn: () => chatbotApi.getMyChatbots(),
    ...config,
  });
}

export function useCreateChatbot() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: ChatbotCreatePayload) => chatbotApi.createChatbot(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.chatbots.lists(),
      });
      showSuccess(t('common.notification.createSuccess'), 'chatbot-create');
    },
  });
}

export function useUpdateChatbot() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: ChatbotUpdatePayload) => chatbotApi.updateChatbot(data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.chatbots.lists(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.chatbots.detail(variables.id),
      });
      showSuccess(t('common.notification.updateSuccess'), 'chatbot-update');
    },
  });
}

export function useDeleteChatbot() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id: string) => chatbotApi.deleteChatbot(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.chatbots.lists(),
      });
      showSuccess(t('common.notification.deleteSuccess'), 'chatbot-delete');
    },
  });
}

export function useAssignChatbotUsers() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      chatbotId,
      userIds,
    }: {
      chatbotId: string;
      userIds: string[];
    }) => chatbotApi.assignUsers(chatbotId, userIds),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.chatbots.users(variables.chatbotId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.chatbots.detail(variables.chatbotId),
      });
      showSuccess(t('common.notification.updateSuccess'), 'chatbot-assign');
    },
  });
}

export function useSortChatbots() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (orderedIds: string[]) => chatbotApi.sortChatbots(orderedIds),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.chatbots.all });
      showSuccess(t('common.notification.updateSuccess'), 'chatbot-sort');
    },
    onError: () => {
      // Trả lại thứ tự thật từ server khi lưu hỏng.
      void queryClient.invalidateQueries({ queryKey: queryKeys.chatbots.all });
    },
  });
}

export function useRemoveChatbotUser() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      chatbotId,
      userId,
    }: {
      chatbotId: string;
      userId: string;
    }) => chatbotApi.removeUser(chatbotId, userId),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.chatbots.users(variables.chatbotId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.chatbots.detail(variables.chatbotId),
      });
      showSuccess(t('common.notification.deleteSuccess'), 'chatbot-remove');
    },
  });
}
